import { ExclamationCircleOutlined, InboxOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputRef,
  message,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select,
  UploadProps
} from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import { VendorsSearch } from 'components/VendorsSearch';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RefSelectProps } from 'antd/lib/select';
import './PurchaseAnalysisModal.scss';
import { Contact, getSfAccountContacts, submitDeal } from 'api/salesforce';
import { useForm } from 'antd/es/form/Form';
import moment, { Moment } from 'moment-timezone';
import { TIMEZONE } from 'shared/constants';
import { RcFile } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import { useRecoilValue } from 'recoil';
import { profileSwitchState } from 'recoil/atoms';
import { ThreeCircles } from 'react-loader-spinner';
import fileUploadValidator from '../../shared/fileUploadValidator';

interface Props extends ModalProps {
  onCancel: () => void;
  onSave: () => void;
}

export interface RawFormData {
  supplierStatus: string;
  subject: string;
  description?: string;
  purchaseType: string;
  compBid?: string;
  datePicker: Moment;
  targetDateReason?: string;
  otherTargetDateReason?: string;
  primaryContact: string;
  selectSupplier: Select;
  selectReseller: Select;
}

export interface Select {
  label: string;
  value: number;
  key: number;
}

/**
 * Represents a modal for purchase analysis.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onCancel - The function to be called when the modal is canceled.
 * @param {Function} props.onSave - The function to be called when the modal is saved.
 * @param {...rest} props.rest - The rest of the props.
 * @returns {React.ReactElement}
 */
export const PurchaseAnalysisModal: React.FC<Props> = ({ onCancel, onSave, ...rest }: Props): React.ReactElement => {
  const MAX_FILES_COUNT = 10;
  const [form] = useForm();
  const supplierSearchRef = useRef<RefSelectProps>(null);
  const targetDateOtherReasonRef = useRef<InputRef>(null);

  // LOCAL STATE
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [fetchingContacts, setFetchingContacts] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);

  // RECOIL STATE
  const profile = useRecoilValue(profileSwitchState);

  /**
   * Resets the form.
   *
   * @callback resetForm
   * @return {void}
   */
  const resetForm = useCallback((): void => {
    setFileList([]);
    setContacts([]);
    setSaving(false);
    setOtherOpen(false);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (rest.open) {
      resetForm();
      setTimeout(() => {
        supplierSearchRef.current?.focus();
      }, 200);
      (async () => {
        try {
          setFetchingContacts(true);
          const contacts: Contact[] | undefined = await getSfAccountContacts(profile?.id);
          if (Array.isArray(contacts)) setContacts(contacts);
        } finally {
          setFetchingContacts(false);
        }
      })();
    }
  }, [profile?.id, resetForm, rest.open]);

  /**
   * Sets the field value for the selectSupplier field with the given supplier id and name.
   *
   * @param {Object} supplier - The supplier object.
   * @param {number} supplier.id - The identifier of the supplier.
   * @param {string} supplier.name - The name of the supplier.
   * @returns {void}
   */
  const onCreateNewSupplier = (supplier: { id: number; name: string }): void => {
    form.setFieldsValue({ selectSupplier: { id: supplier.id, label: supplier.name } });
  };

  /**
   * Updates the form's selectReseller field value with the given reseller's id and name.
   *
   * @param {object} reseller - The reseller object containing an id and name property.
   * @returns {void}
   */
  const onCreateNewReseller = (reseller: { id: number; name: string }): void => {
    form.setFieldsValue({ selectReseller: { id: reseller.id, label: reseller.name } });
  };

  /**
   * Display a waiting modal by showing three rotating circles.
   * This modal is typically used to indicate that a task is in progress and the user needs to wait.
   *
   * @function waitingModal
   * @returns {void}
   */
  const waitingModal = (): void => {
    Modal.info({
      title: 'Submitting ...',
      content: (
        <div style={{ paddingLeft: 100, paddingTop: 20 }}>
          <ThreeCircles
            height="80"
            width="80"
            color="#4fa94d"
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
            ariaLabel="three-circles-rotating"
            outerCircleColor=""
            innerCircleColor="#152d51"
            middleCircleColor=""
          />
        </div>
      ),
      closable: false,
      centered: true,
      okButtonProps: { style: { display: 'none' } }
    });
  };

  /**
   * Handles the change of target date reason.
   *
   * @param {any} val - The new value of the target date reason.
   * @returns {void}
   */
  const handleTargetDateReasonChange = (val: any): void => {
    if (val === 'Other') {
      setOtherOpen(true);
      setTimeout(() => {
        targetDateOtherReasonRef.current?.focus();
      }, 100);
    } else {
      setOtherOpen(false);
      form.setFieldsValue({ otherTargetDateReason: undefined });
    }
  };

  /**
   * Submits the form data and handles the submission process.
   *
   * @param {boolean} ignoreFiles - Determines whether to ignore missing files or not.
   * @returns {Promise<void>} - A promise that resolves with no value.
   */
  const handleSubmit = useCallback(
    async (ignoreFiles: boolean): Promise<void> => {
      let data: RawFormData;
      try {
        data = await form.validateFields();
      } catch (e) {
        message.error('Please fill in all required fields');
        return;
      }

      if (fileList.length === 0 && !ignoreFiles) {
        Modal.confirm({
          title: 'Submit with no proposal or contract?',
          icon: <ExclamationCircleOutlined />,
          content: 'Are you sure you wish to submit without including a proposal or contract?',
          onOk(): void {
            handleSubmit(true);
          }
        });
        return;
      } else {
        const targetDate = moment(data.datePicker).tz(TIMEZONE).format('YYYY-MM-DD');

        /**
         * Represents a formData object for handling form data in the browser.
         *
         * @class
         */
        const formData = new FormData();
        formData.append('vendor_id', data.selectSupplier.value ? String(data.selectSupplier.value) : '-1');
        formData.append('vendor_name', data.selectSupplier.label);
        if (data.selectReseller) {
          formData.append('reseller_id', data.selectReseller.value ? String(data.selectReseller.value) : '-1');
          formData.append('reseller_name', data.selectReseller.label);
        }
        formData.append('subject', data.subject);
        if (data.description) formData.append('description', data.description);
        formData.append('purchaseType', data.purchaseType);
        if (data.compBid) formData.append('compBid', data.compBid);
        if (otherOpen && data.otherTargetDateReason) {
          formData.append('targetDateReason', data.otherTargetDateReason);
        } else if (!otherOpen && data.targetDateReason) {
          formData.append('targetDateReason', data.targetDateReason);
        }
        formData.append('targetDate', targetDate);
        formData.append('primaryContact', data.primaryContact);

        fileList.forEach((file) => {
          formData.append(`files`, file as RcFile);
        });

        try {
          setSaving(true);
          waitingModal();
          await submitDeal(formData, profile?.id);
          message.success('Deal submitted successfully!');
          onSave();
        } catch (e) {
          message.error('There was a problem submitting the deal.');
        } finally {
          setSaving(false);
          Modal.destroyAll();
        }
      }
    },
    [fileList, form, onSave, otherOpen, profile?.id]
  );

  /**
   * Represents the upload properties.
   *
   * @typedef {Object} UploadProps
   * @property {Function} onRemove - Callback function triggered when a file is removed from fileList.
   *   @param {File} file - The file to be removed.
   * @property {Function} beforeUpload - Callback function triggered before file upload.
   *   @param {File} _file - The file to be uploaded.
   *   @param {File[]} _fileList - The list of files to be uploaded.
   *   @returns {boolean} - Whether the file upload should continue or not.
   * @property {boolean} multiple - Whether multiple files can be uploaded at once.
   * @property {File[]} fileList - The list of files to be uploaded.
   */
  const uploadProps: UploadProps = {
    onRemove: (file): void => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (_file: RcFile, _fileList: RcFile[]) => {
      if (!fileUploadValidator(_file, fileList, MAX_FILES_COUNT)) {
        return false;
      }
      setFileList((prevState) => {
        if (prevState.length < MAX_FILES_COUNT) {
          return [...prevState, _file];
        } else {
          message.error(`You are allowed to upload only a maximum of ${MAX_FILES_COUNT} files`).then();
          return [...prevState];
        }
      });
      return false;
    },
    multiple: true,
    fileList
  };

  /**
   * Function that handles cancel event.
   *
   * @param {any} e - The event object.
   * @returns {void} - No return value.
   */
  const handleOnCancelEvent = (e: any): void => {
    if (e.type === 'keydown' && e.key === 'Escape') {
      e.preventDefault();
      return;
    } else if (e.type === 'click' && e.target?.className === 'ant-modal-wrap') {
      e.preventDefault();
      return;
    }
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      {...rest}
      title={'Submit a purchase for analysis'}
      width={980}
      className="purchase-analysis-modal"
      onCancel={handleOnCancelEvent}
      onOk={() => handleSubmit(false)}
      okText={'Submit'}
      closable={!saving}
      okButtonProps={{ loading: saving }}
    >
      <Row gutter={24}>
        <Col span={15}>
          <div className="required-field mb-3 -mt-3">indicates a required field</div>
          <Form
            form={form}
            labelAlign="left"
            size="small"
            layout="vertical"
            colon={false}
            labelWrap
            requiredMark={true}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="selectSupplier"
                  label="Select supplier (OEM) for this purchase"
                  rules={[{ required: true, message: 'A supplier is required' }]}
                >
                  <VendorsSearch
                    ref={supplierSearchRef}
                    labelInValue
                    size={'large'}
                    placeholder={'Search for supplier'}
                    suffixIcon={<SearchOutlined style={{ fontSize: 18 }} />}
                    style={{ width: '100%' }}
                    allowCreate={true}
                    onCreateNew={onCreateNewSupplier}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="selectReseller" label="Select Reseller">
                  <VendorsSearch
                    labelInValue
                    placeholder={'Search for reseller'}
                    reseller
                    suffixIcon={<SearchOutlined style={{ fontSize: 18 }} />}
                    style={{ width: '100%' }}
                    allowCreate={true}
                    onCreateNew={onCreateNewReseller}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="subject"
              label="High-level description of purchase"
              rules={[{ required: true, message: 'A high-level subject is required' }]}
            >
              <Input.TextArea
                className="rounded-md"
                rows={1}
                size={'large'}
                placeholder="e.g., Security software, Software licenses, etc."
                maxLength={255}
                showCount={true}
              />
            </Form.Item>

            <Form.Item name="purchaseType" label="Purchase Type" rules={[{ required: true }]}>
              <Radio.Group>
                <Radio value={'Renewal'}>Renewal</Radio>
                <Radio value={'Add-On Purchase'}>Add-On Purchase</Radio>
                <Radio value={'New Purchase'}>New Purchase</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="compBid" label="Do you have competitive bids for this purchase?">
              <Radio.Group>
                <Radio value={'True'}>Yes</Radio>
                <Radio value={'False'}>No</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="datePicker"
              label="What is the target date for purchase completion?"
              rules={[{ required: true, message: 'Target date is required' }]}
            >
              <DatePicker size="large" className="rounded-md" format={'MMM DD, YYYY'} />
            </Form.Item>
            <Row gutter={0}>
              <Col span={12}>
                <Form.Item name="targetDateReason" label="What is driving the target date?">
                  <Select
                    size="large"
                    style={{ width: 260 }}
                    onChange={handleTargetDateReasonChange}
                    placeholder="Select"
                    options={[
                      {
                        value: 'Expiring contract',
                        label: 'Expiring contract'
                      },
                      {
                        value: 'Businesss requirement',
                        label: 'Business requirements'
                      },
                      {
                        value: 'Early renewal offer',
                        label: 'Early renewal offer'
                      },
                      {
                        value: 'Incremental need',
                        label: 'Incremental need'
                      },
                      {
                        value: 'Other',
                        label: <div style={{ fontStyle: 'italic', color: '#777' }}>Other</div>
                      }
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {otherOpen && (
                  <Form.Item name="otherTargetDateReason" label="&nbsp;" requiredMark={false}>
                    <Input
                      size="large"
                      placeholder="Other reason"
                      className="rounded-md"
                      ref={targetDateOtherReasonRef}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Form.Item name="description" label="Notes/Context">
              <Input.TextArea
                className="rounded-md"
                size={'large'}
                rows={2}
                placeholder="Provide relevant notes/questions/context here"
              />
            </Form.Item>

            <Form.Item
              name="primaryContact"
              label="Primary contact"
              rules={[{ required: true, message: 'A primary contact is required' }]}
            >
              <Select
                className="mb-4"
                style={{ width: 260 }}
                size="large"
                placeholder="Primary contact"
                loading={fetchingContacts}
                fieldNames={{ label: 'Name', value: 'Id' }}
                options={contacts}
                virtual={false}
                showSearch={true}
                optionFilterProp="Name"
                optionLabelProp="Name"
              />
            </Form.Item>

            {/* end of form */}
          </Form>
        </Col>
        {/* Left Col Ends*/}

        <Col span={9}>
          <Dragger className="p-4 pb-0 mb-2" maxCount={MAX_FILES_COUNT} {...uploadProps}>
            <div className="dragger-content">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file(s) to this area to upload, up to {MAX_FILES_COUNT}</p>
              <p className="ant-upload-hint">
                Please include the most recent quote provided by the supplier. If this submission is for a renewal,
                please also include the quote/contract for the expiring term.
              </p>
            </div>
          </Dragger>
        </Col>
        {/* Right Cols Ends */}
      </Row>
    </Modal>
  );
};
